import styled from "@emotion/styled";
import { ButtonClickAnalyticsType, FieldTypeOption } from "@kaltura/mediaspace-shared-types";
import { NameField } from "@kaltura/mediaspace-shared-ui";
import { FieldValues, FormProvider, useForm } from "react-hook-form";
import { baseUrl, postKmsData, translate, useGuessedTimeZone } from "@kaltura/mediaspace-shared-utils";
import { useButtonAnalytics } from "@kaltura/mediaspace-shared-hooks";
import { useToastsContext } from "@kaltura/mediaspace-shared-contexts/ToastsContext";
import { Template, Schedule } from "@kaltura/mediaspace-shared-ui/controlled-form";
import { DVR } from "./dvr/dvr";
import { KalturaRecordStatus } from "kaltura-typescript-client/api/types/KalturaRecordStatus";
import {
    ControlledVideoSourceSection,
    LiveStudioTypeSelection,
    RecordingMode,
    VideoSourceSectionProps,
} from "@kaltura/mediaspace-shared-edit-townhall";

export interface CreateTownhallFormProps {
    /**
     * mode of the townhall
     */
    mode?: "kwebcast" | "live-studio" | "simulive";

    /**
     * list of available timezones
     */
    timezones: FieldTypeOption[];

    /**
     * default recording mode to use in the form (skip to hide the field)
     */
    defaultRecordingMode?: KalturaRecordStatus;

    /**
     * Path part of the URL to which the form data will be submitted (will be appended to baseUrl)
     */
    submissionUrl: string;

    /**
     * Function to handle form submission
     * @param data - form data
     */
    onSubmit: (data: { [key: string]: string | boolean }) => Promise<void>;

    /**
     * Form ID for accessibility purposes
     */
    formId: string;

    /**
     * Function to update parent's processing state
     */
    updateProcessing: (value: boolean) => void;

    /**
     * This is KMS' global DVR config. When enabled - a kWebcast creator can decide whether to enable DVR individually, per entry.
     */
    dvrEnabled: boolean;

    /**
     * function to get list of templates to display in the form
     */
    fetchTemplates?: () => Template[];

    /**
     * Props for the Video Source component. Skip to hide.
     */
    videoSourceProps?: VideoSourceSectionProps;
}

const StyledForm = styled("form")(({ theme }) => ({
    display: "flex",
    flexDirection: "column",
    gap: theme.spacing(3),
}));

const baseClassName = "kms-ds-create-townhall-form";

/**
 * Create Townhall Form component
 */
const CreateTownhallForm = (props: CreateTownhallFormProps) => {
    const {
        mode = "kwebcast",
        timezones,
        defaultRecordingMode,
        dvrEnabled,
        formId,
        updateProcessing,
        submissionUrl,
        fetchTemplates,
        videoSourceProps,
    } = props;

    const sendButtonAnalytics = useButtonAnalytics();

    const { showToast } = useToastsContext();

    const guessedTz = useGuessedTimeZone();

    const templates = fetchTemplates?.();

    const useFormHookMethods = useForm({
        mode: "onChange",
        defaultValues: {
            name: "",
            tz: guessedTz,
            ...(templates && templates.length ? { roomMode: templates[0].id } : {}),
        },
    });

    const onSubmit = async (data: FieldValues) => {
        updateProcessing(true);
        switch (mode) {
            case "kwebcast":
                sendButtonAnalytics("Add - Webcast", ButtonClickAnalyticsType.CREATE);
                break;
            case "live-studio":
                sendButtonAnalytics("Add - Live-studio", ButtonClickAnalyticsType.CREATE);
                break;
            case "simulive":
                sendButtonAnalytics("Add - Simulive", ButtonClickAnalyticsType.CREATE);
                break;
        }

        try {
            const result = await postKmsData(`${baseUrl}${submissionUrl}`, data);
            if (result.success && result.redirectUrl) {
                // redirect to the new townhall page on success
                window.location.href = result.redirectUrl;
            }
            if (!result.success) {
                // show toast in case of other errors
                showToast({ severity: "error", message: result.errorMessage });
                updateProcessing(false);
            }
        }
        catch (e) {
            updateProcessing(false);
            showToast({ severity: "error", message: translate("Failed to create town hall") });
        }
    };

    return (
        <FormProvider {...useFormHookMethods}>
            <StyledForm className={baseClassName} id={formId} onSubmit={useFormHookMethods.handleSubmit(onSubmit)}>
                <NameField id={"name"} label={translate("Name")} className={`${baseClassName}-name`} />
                {templates && templates.length > 0 && <LiveStudioTypeSelection templates={templates} />}
                <Schedule timezones={timezones} />
                {defaultRecordingMode !== undefined && (
                    <RecordingMode
                        id={"recordingMode"}
                        label={translate("Event Recording")}
                        value={defaultRecordingMode}
                        className={"kms-ds-create-townhall-form-event-recording"}
                        inline={true}
                    />
                )}
                {dvrEnabled ? <DVR /> : null}
                {videoSourceProps && <ControlledVideoSourceSection id={"videoSourceEntryId"} {...videoSourceProps} />}
                <input type="submit" hidden aria-hidden="true" />
            </StyledForm>
        </FormProvider>
    );
};

export default CreateTownhallForm;
